// Lib
import React from 'react';
import { Editor, useEditor } from '@tiptap/react';
import { Extensions } from '@tiptap/core';

// Hooks
import useTiptapTriggerEditable from './hooks/useTiptapTriggerEditable';
import useTiptapEditorFocus from './hooks/useTiptapEditorFocus';
import useTiptapEditorPersistedContentSync, {
    PersistedContentSyncMode,
} from './hooks/useTiptapEditorPersistedContentSync';
import useSyncTiptapActiveEditorState from './hooks/useSyncTiptapActiveEditorState';
import { usePreservedSelection } from './extensions/VirtualSelection';
import useElementTiptapEditorClickHandler from './hooks/useElementTiptapEditorClickHandler';

// Types
import { TiptapContent } from './tiptapTypes';
import { Rect } from '../maths/geometry/rect/rectTypes';
import { PlatformDetails } from '../platform/platformTypes';
import { SaveTiptapEditorContentFunction } from './extensions/externalStoreSync/externalStoreSynchroniserTypes';
import { QuotationCssCustomProperties } from './utils/quotationCssCustomProperties';

// General Milanote Tiptap editor styles
import './styles/MilanoteTiptapEditorStyles.scss';

export type UseTiptapEditorProps = {
    persistedContent: TiptapContent | null;
    persistedContentSyncMode?: PersistedContentSyncMode;
    extensions: Extensions;
    editorClassname?: string;
    quotationCssCustomProperties?: QuotationCssCustomProperties;

    editorId: string;
    editorKey: string;
    currentEditorId?: string;

    isEditable: boolean;
    isEditing: boolean;
    isSingleSelected: boolean;
    registryEditorId?: string;

    startEditing?: (args: { editorId: string; editorKey: string; editorFocusRect: Rect }) => void;

    saveContent?: SaveTiptapEditorContentFunction;
    updateActiveEditorStore?: (editor: Editor | null) => void;
    platformDetails: PlatformDetails;

    preventInitialFocus?: boolean;
    // WEB-13628 - This is a (ideally) temporary flag to fix an issue with scrolls occurring while de-indenting tasks.
    //  It can be removed if task lists become a single editor
    _preventScroll?: boolean;
};

export type UseTiptapEditorReturn = {
    editor: ReturnType<typeof useEditor>;
    onClick: (event: React.MouseEvent) => void;
};

/**
 * Creates the Tiptap editor instance and sets up the necessary behaviours for it, such as:
 * - Making the editor editable when it is the currently edited editor.
 * - Saving the content on a de-bounce.
 * - Syncing the active editor to the context.
 * - Focusing the editor when it starts editing.
 * - Preventing mousedown events from reaching the Element when the editor is being edited.
 * - Starting editing when the Element is clicked.
 */
const useElementTiptapEditor = ({
    persistedContent,
    persistedContentSyncMode = PersistedContentSyncMode.DEFERRED,
    extensions,
    editorId,
    editorKey,
    currentEditorId,
    isEditing,
    isEditable,
    isSingleSelected,
    startEditing,
    saveContent,
    updateActiveEditorStore,
    editorClassname = '',
    quotationCssCustomProperties,
    platformDetails,
    preventInitialFocus = false,
    // WEB-13628 - Temporary flag to avoid issues when de-indenting tasks
    _preventScroll,
}: UseTiptapEditorProps): UseTiptapEditorReturn => {
    const quotationCssVariableStyleString = quotationCssCustomProperties
        ? Object.entries(quotationCssCustomProperties)
              .map(([cssVariable, cssValue]) => `${cssVariable}: ${cssValue};`)
              .join(' ')
        : '';

    const editor = useEditor({
        extensions,
        content: persistedContent,
        editorProps: {
            attributes: {
                class: editorClassname,
                ...(quotationCssVariableStyleString ? { style: quotationCssVariableStyleString } : {}),
            },
            // prevent standard prosemirror scrolling behaviour
            handleScrollToSelection: () => true,
        },
    }) as Editor;

    const isEditingThisEditor = currentEditorId === editorId && isEditing;

    // Only allow the editor to be editable if it is the currently edited editor.
    //  If we don't do this, we won't be able to drag on the editor, it would instead select the text
    useTiptapTriggerEditable(editor, isEditable && isEditingThisEditor);
    // Sync the active editor to the context
    useSyncTiptapActiveEditorState(editor, isEditingThisEditor, updateActiveEditorStore);

    // Ensure the cursor is in the editor while editing
    useTiptapEditorFocus(editor, isEditingThisEditor, preventInitialFocus, _preventScroll);

    // Ensure updates made to the content, while it's not being edited (e.g. undo/redo or remote editing),
    // are reflected in the editor
    useTiptapEditorPersistedContentSync(editor, persistedContent, isEditingThisEditor, persistedContentSyncMode);

    // Keep selections visible while popups are open
    usePreservedSelection(editor, isSingleSelected, isEditingThisEditor);

    /**
     * Start editing this element when it's clicked.
     */
    const onClick = useElementTiptapEditorClickHandler({
        isSingleSelected,
        editor,
        isEditingThisEditor,
        isEditable,
        editorId,
        editorKey,
        platformDetails,
        startEditing,
    });

    return { editor, onClick };
};

export default useElementTiptapEditor;
